/**
 * Janela de inatividade antes de o app ser considerado ocioso.
 *
 * 60s, não os 30s da proposta original: engajamento passivo — ler uma missão
 * longa, assistir a um `live-stream` — passa minutos sem tocar a tela, e nada
 * nesta política é urgente. Errar para mais custa um participante alguns
 * minutos numa versão velha; errar para menos custa o progresso dele.
 */
export const IDLE_WINDOW_MS = 60_000

/** Sinais que significam "tem gente usando isto agora". */
const ACTIVITY_EVENTS = [
  'mousemove',
  'touchstart',
  'keydown',
  'visibilitychange',
  'focus',
  'blur'
] as const

type ActivityTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>

type IdleManagerOptions = {
  /** Injetado para que o núcleo não dependa de `window` — e rode em teste `node`. */
  target?: ActivityTarget
  onIdle: () => void
  windowMs?: number
}

/**
 * Observa atividade do usuário e avisa quando ela cessa pela janela configurada.
 *
 * Sem React e sem `window` implícito: quem monta injeta o alvo. É o que permite
 * testar a fiação — e era exatamente a fiação, não a função pura, que continha
 * o bug do PR #85.
 */
export const createIdleManager = ({
  target,
  onIdle,
  windowMs = IDLE_WINDOW_MS
}: IdleManagerOptions) => {
  const alvo =
    target ?? (typeof window === 'undefined' ? null : (window as ActivityTarget))

  let timer: ReturnType<typeof setTimeout> | null = null
  let ocioso = false
  let ativo = false

  const limparTimer = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
  }

  const agendar = () => {
    limparTimer()
    timer = setTimeout(() => {
      ocioso = true
      onIdle()
    }, windowMs)
  }

  const aoInteragir = () => {
    if (!ativo) return

    ocioso = false
    agendar()
  }

  return {
    start: () => {
      if (ativo) return

      ativo = true
      ACTIVITY_EVENTS.forEach(evento =>
        alvo?.addEventListener(evento, aoInteragir)
      )
      agendar()
    },

    dispose: () => {
      if (!ativo) return

      ativo = false
      limparTimer()
      ocioso = false
      ACTIVITY_EVENTS.forEach(evento =>
        alvo?.removeEventListener(evento, aoInteragir)
      )
    },

    isIdle: () => ocioso
  }
}

export type IdleManager = ReturnType<typeof createIdleManager>
